import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import { useBreadcrumb } from '../layouts/BreadcrumbContext';
import { NavLink } from 'react-router-dom';
import InlineLoader from '../components/Loader/InlineLoader';
import SendExchangeModal from '../components/Modal/SendExchangeModal';
import ReceiveExchangeModal from '../components/Modal/ReceiveExchangeModal';
import EntityTable from '../components/Listing/EntityTable';
import FabGroup from '../components/FabGroup';
import Modal from '../components/Modal/Modal';
import { toast } from 'react-toastify';
import { formatPrice } from '../utils/FormatPrice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faPaperPlane, faArrowLeft, faArrowUp, faArrowDown, faUser, faEllipsisH, faPen } from '@fortawesome/free-solid-svg-icons';
import Select from '../components/Form/Fields/Select';
import PharmacyAvatar from '../components/Pharmacy/PharmacyAvatar';
import LaboratoryLogo from '../components/Laboratory/LaboratoryLogo';
import { API_BASE_URL } from '../api';

const getExchangeTypeOptions = (t) => [
    { value: '', label: t('pharmacy_details.type.all') },
    { value: 'SENT', label: t('pharmacy_details.type.sent') },
    { value: 'RECEIVED', label: t('pharmacy_details.type.received') },
];

const INFO_BADGE_STYLE = {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 12, fontWeight: 500, color: '#374151',
    background: '#f9fafb', border: '1px solid #e5e7eb',
    borderRadius: 20, padding: '4px 10px', whiteSpace: 'nowrap',
};

const exchangeColumns = (pharmacyId, onEdit, t) => [
    {
        Header: t('pharmacy_details.column.type'),
        accessor: 'type',
        filterable: { component: Select, options: getExchangeTypeOptions(t) },
        Cell: (row) => {
            const isSent = row.fromPharmacyId !== pharmacyId;
            return (
                <span className={`badge ${isSent ? 'bg-primary' : 'bg-success'}`}>
                    {isSent ? '↑' : '↓'} {isSent ? t('pharmacy_details.type.sent') : t('pharmacy_details.type.received')}
                </span>
            );
        },
    },
    {
        Header: t('pharmacy_details.column.date'),
        accessor: 'exchangedAt',
        sortable: true,
        Cell: (row) => (
            <span style={{ color: '#6b7280', fontSize: 13 }}>
                {new Date(row.exchangedAt).toLocaleDateString('fr-FR', {
                    day: '2-digit', month: 'short', year: 'numeric',
                    hour: '2-digit', minute: '2-digit'
                })}
            </span>
        ),
    },
    {
        Header: t('Products'),
        accessor: 'items',
        Cell: (row) => (
            <div className="d-flex flex-column gap-2">
                {row.items.map((item, i) => (
                    <div key={i}>
                        <NavLink to={`/products/${item.orderableId}`} className="fw-semibold" style={{ fontSize: 13 }}>
                            {item.orderableName}
                        </NavLink>
                        {(item.presentation || item.dosage) && (
                            <div style={{ fontSize: 12, color: '#9ca3af' }}>
                                {item.presentation}{item.presentation && item.dosage ? ' · ' : ''}{item.dosage}
                            </div>
                        )}
                        {item.laboratoryName && (
                            <NavLink
                                to={`/suppliers/${item.laboratoryId}`}
                                className="text-muted"
                                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, textDecoration: 'none' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <LaboratoryLogo id={item.laboratoryId} name={item.laboratoryName} size={14} />
                                {item.laboratoryName}
                            </NavLink>
                        )}
                    </div>
                ))}
            </div>
        ),
    },
    {
        Header: t('Quantity'),
        accessor: 'quantity',
        className: 'text-center',
        Cell: (row) => (
            <div className="d-flex flex-column gap-1" style={{ fontSize: 13 }}>
                {row.items.map((item, i) => (
                    <div key={i}>× {item.quantity}</div>
                ))}
            </div>
        ),
    },
    {
        Header: t('Price'),
        accessor: 'price',
        className: 'text-end',
        Cell: (row) => (
            <div className="d-flex flex-column gap-1" style={{ fontSize: 13 }}>
                {row.items.map((item, i) => (
                    <div key={i} className="fw-semibold">
                        {item.totalPrice != null ? formatPrice(item.totalPrice) : '—'}
                    </div>
                ))}
            </div>
        ),
    },
    {
        Header: t('pharmacy_details.column.notes'),
        accessor: 'notes',
        Cell: (row) => row.notes
            ? <span className="text-muted small fst-italic">{row.notes}</span>
            : <span className="cell-muted">—</span>,
    },
    {
        Header: '',
        accessor: 'actions',
        className: 'col-actions',
        Cell: (row) => (
            <div className="cell-dropdown">
                <button className="cell-actions-btn dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                    <FontAwesomeIcon icon={faEllipsisH} />
                </button>
                <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                        <button className="dropdown-item" onClick={() => onEdit(row)}>
                            <FontAwesomeIcon icon={faPen} className="me-2 text-muted" />
                            {t('Edit')}
                        </button>
                    </li>
                </ul>
            </div>
        ),
    },
];

export default function PharmacyDetails() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [pharmacy, setPharmacy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sendTarget, setSendTarget] = useState(null);
    const [receiveTarget, setReceiveTarget] = useState(null);
    const [totals, setTotals] = useState(null);
    const [editingExchange, setEditingExchange] = useState(null);
    const [notesDraft, setNotesDraft] = useState('');
    const [savingNotes, setSavingNotes] = useState(false);
    const exchangesTableRef = useRef(null);
    const { setTitle, setTitleAvatar, setTitleDescription } = usePageTitle();
    const { setBreadcrumb } = useBreadcrumb();

    const loadTotals = () => {
        axios
            .get(`${API_BASE_URL}/api/v1/exchanges/with/${id}/totals`, { withCredentials: true })
            .then(({ data }) => setTotals(data))
            .catch(console.error);
    };

    const handleEditExchange = (exchange) => {
        setEditingExchange(exchange);
        setNotesDraft(exchange.notes || '');
    };

    const handleSaveNotes = () => {
        if (!editingExchange) return;
        setSavingNotes(true);
        axios.patch(
            `${API_BASE_URL}/api/v1/exchanges/${editingExchange.id}/notes`,
            { notes: notesDraft },
            { withCredentials: true }
        )
            .then(() => {
                toast.success(t('pharmacy_details.toast.exchange_updated'));
                setEditingExchange(null);
                exchangesTableRef.current?.reload();
            })
            .catch(() => toast.error(t('pharmacy_details.toast.update_error')))
            .finally(() => setSavingNotes(false));
    };

    useEffect(() => {
        axios
            .get(`${API_BASE_URL}/api/v1/pharmacies/${id}`, { withCredentials: true })
            .then(({ data }) => {
                setPharmacy(data);
                const addr = data.addresses?.[0];
                const fullAddress = addr && [
                    addr.address,
                    addr.address2,
                    [addr.postalCode, addr.city].filter(Boolean).join(' '),
                ].filter(Boolean).join(', ');
                setTitle(data.name);
                setTitleDescription(fullAddress || null);
                setTitleAvatar(<PharmacyAvatar pharmacy={data} size={36} />);
                document.title = data.name;
                setBreadcrumb([
                    { label: t('Confrères'), url: '/confreres' },
                    { label: data.name },
                ]);
            })
            .catch(console.error)
            .finally(() => setLoading(false));

        loadTotals();
        return () => { setTitleAvatar(null); setTitleDescription(null); };
    }, [id, t]);

    if (loading) return <InlineLoader loadingMessage={t('Loading...')} />;
    if (!pharmacy) return <div className="page-content text-muted p-4">{t('pharmacy_details.not_found')}</div>;

    const addr = pharmacy.addresses?.[0];
    const exchangeCols = exchangeColumns(pharmacy.id, handleEditExchange, t);
    const exchangeActionsColumn = exchangeCols.find((c) => c.accessor === 'actions');

    // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
    // — reuses the actions column's own dropdown so both views offer identical actions.
    const renderExchangeCard = (row) => {
        const isSent = row.fromPharmacyId !== pharmacy.id;
        return (
            <div className="card h-100 shadow-sm">
                <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
                    <span className={`badge ${isSent ? 'bg-primary' : 'bg-success'}`} style={{ alignSelf: 'flex-start' }}>
                        {isSent ? '↑' : '↓'} {isSent ? t('pharmacy_details.type.sent') : t('pharmacy_details.type.received')}
                    </span>
                    <span style={{ fontSize: 11.5, color: '#9ca3af' }}>
                        {new Date(row.exchangedAt).toLocaleDateString('fr-FR', {
                            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                    </span>

                    <div className="d-flex flex-column gap-1" style={{ marginTop: 2 }}>
                        {row.items.map((item, i) => (
                            <div key={i} style={{ fontSize: 12.5, color: '#374151' }}>
                                {item.orderableName} <span style={{ color: '#9ca3af' }}>× {item.quantity}</span>
                            </div>
                        ))}
                    </div>

                    {row.notes && (
                        <span style={{ fontSize: 11.5, color: '#9ca3af', fontStyle: 'italic' }}>{row.notes}</span>
                    )}

                    <div className="d-flex align-items-center justify-content-between" style={{ marginTop: 'auto', paddingTop: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>
                            {formatPrice(row.items.reduce((sum, item) => sum + (item.totalPrice || 0), 0))}
                        </span>
                        {exchangeActionsColumn?.Cell(row)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* Pharmacy info — compact bar */}
            <div style={{
                flexShrink: 0,
                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 12,
                padding: '12px 20px',
                borderBottom: '1px solid #f0f1f3',
                background: '#fff',
            }}>
                {totals && (
                    <div className="d-flex gap-2">
                        <div className="d-flex align-items-center gap-2" style={{ background: '#eff6ff', borderRadius: 10, padding: '6px 12px' }}>
                            <span style={{
                                width: 26, height: 26, borderRadius: 7,
                                background: '#dbeafe', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <FontAwesomeIcon icon={faArrowUp} style={{ color: '#1d4ed8', fontSize: 11 }} />
                            </span>
                            <div>
                                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>{t('pharmacy_details.stat.total_sent')}</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8' }}>{formatPrice(totals.totalSent)}</div>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2" style={{ background: '#ecfdf5', borderRadius: 10, padding: '6px 12px' }}>
                            <span style={{
                                width: 26, height: 26, borderRadius: 7,
                                background: '#d1fae5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}>
                                <FontAwesomeIcon icon={faArrowDown} style={{ color: '#059669', fontSize: 11 }} />
                            </span>
                            <div>
                                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600 }}>{t('pharmacy_details.stat.total_received')}</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#059669' }}>{formatPrice(totals.totalReceived)}</div>
                            </div>
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginLeft: 'auto' }}>
                    {pharmacy.ownerName && (
                        <span style={INFO_BADGE_STYLE}>
                            <FontAwesomeIcon icon={faUser} style={{ fontSize: 11 }} />
                            {pharmacy.ownerName}
                        </span>
                    )}
                    {pharmacy.email && (
                        <span style={INFO_BADGE_STYLE}>
                            <FontAwesomeIcon icon={faEnvelope} style={{ fontSize: 11 }} />
                            {pharmacy.email}
                        </span>
                    )}
                    {addr?.telephone && (
                        <span style={INFO_BADGE_STYLE}>
                            <FontAwesomeIcon icon={faPhone} style={{ fontSize: 11 }} />
                            {addr.telephone}
                        </span>
                    )}
                </div>
            </div>

            <div style={{ flex: 1, minHeight: 0 }}>
                <EntityTable
                    ref={exchangesTableRef}
                    endpoint={`${API_BASE_URL}/api/v1/exchanges/with/${id}`}
                    columns={exchangeCols}
                    defaultSort="exchangedAt"
                    defaultDirection="desc"
                    pageSize={20}
                    searchable={true}
                    multiSelect={true}
                    searchPlaceholder={t('pharmacy_details.search_placeholder')}
                    enableCardView={true}
                    renderCard={renderExchangeCard}
                />
            </div>

            {/* Modal envoi */}
            <SendExchangeModal
                show={!!sendTarget}
                onClose={() => { setSendTarget(null); exchangesTableRef.current?.reload(); loadTotals(); }}
                targetPharmacy={sendTarget}
            />

            {/* Modal récupération */}
            <ReceiveExchangeModal
                show={!!receiveTarget}
                onClose={() => { setReceiveTarget(null); exchangesTableRef.current?.reload(); loadTotals(); }}
                sourcePharmacy={receiveTarget}
            />

            <FabGroup
                buttons={[
                    { id: 'send', label: t('pharmacy_details.action.send_products'), icon: faPaperPlane, onClick: () => setSendTarget(pharmacy) },
                    { id: 'receive', label: t('pharmacy_details.action.receive_products'), icon: faArrowLeft, onClick: () => setReceiveTarget(pharmacy) },
                ]}
            />

            {/* Modal modification (notes uniquement — les produits/quantités d'un échange déjà
                validé ne peuvent pas être modifiés sans risquer de désynchroniser le stock) */}
            <Modal
                show={!!editingExchange}
                title={t('pharmacy_details.modal.edit_title')}
                onClose={() => setEditingExchange(null)}
                classSize="modal-sm"
                footer={
                    <>
                        <button className="btn btn-light btn-sm" onClick={() => setEditingExchange(null)}>{t('Cancel')}</button>
                        <button className="btn btn-primary btn-sm fw-bold" onClick={handleSaveNotes} disabled={savingNotes}>
                            {savingNotes ? t('pharmacy_details.modal.saving') : t('Save')}
                        </button>
                    </>
                }
            >
                <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>
                    {t('pharmacy_details.modal.edit_notice')}
                </p>
                <label className="form-label" style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{t('pharmacy_details.column.notes')}</label>
                <textarea
                    className="form-control"
                    rows={4}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    placeholder={t('pharmacy_details.modal.notes_placeholder')}
                />
            </Modal>
        </div>
    );
}
